import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, AlertTriangle, Shield, Volume2, Settings2, Check } from 'lucide-react';
import { useVoice } from '../context/VoiceContext';
import { GlassCard } from './layout/GlassCard';
import { toast } from 'sonner';

export default function AlwaysListeningSettings() {
  const { 
    alwaysListening,
    toggleAlwaysListening,
    wakeWord,
    setWakeWord,
    isWakeWordListening,
    wakeWordDetected
  } = useVoice();
  
  const [showPrivacyWarning, setShowPrivacyWarning] = useState(false);
  const [customWakeWord, setCustomWakeWord] = useState(wakeWord);

  const handleToggle = () => {
    if (!alwaysListening) {
      // Show privacy warning before enabling
      setShowPrivacyWarning(true);
    } else {
      toggleAlwaysListening(false);
      toast.success('Always Listening mode disabled');
    }
  };

  const confirmEnable = () => {
    toggleAlwaysListening(true);
    setShowPrivacyWarning(false);
    toast.success('Always Listening mode enabled. Say "Hey AIRA" to activate!');
  };

  const handleWakeWordChange = () => {
    if (customWakeWord.trim()) {
      setWakeWord(customWakeWord.trim().toLowerCase());
      toast.success(`Wake word updated to "${customWakeWord.trim()}"`);
    }
  };

  const wakeWordSuggestions = [
    'hey aira',
    'hello aira',
    'hi aira',
    'aira'
  ];

  return (
    <GlassCard className="p-6" data-testid="always-listening-settings">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          alwaysListening 
            ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
            : 'bg-gradient-to-r from-gray-600 to-gray-700'
        }`}>
          {alwaysListening ? (
            <Mic className="w-5 h-5 text-white" />
          ) : (
            <MicOff className="w-5 h-5 text-white" />
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-[var(--text-primary)]">Always Listening Mode</h3>
          <p className="text-sm text-[var(--text-tertiary)]">
            Say "{wakeWord}" to activate AIRA hands-free
          </p>
        </div>
        <button
          onClick={handleToggle}
          className={`relative w-14 h-7 rounded-full transition-colors ${
            alwaysListening ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-600'
          }`}
          data-testid="always-listening-toggle"
        >
          <motion.div
            className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
            animate={{ left: alwaysListening ? '32px' : '4px' }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </button>
      </div>

      {/* Status Indicator */}
      <AnimatePresence>
        {alwaysListening && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <div className={`p-4 rounded-xl border ${
              isWakeWordListening 
                ? wakeWordDetected
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-accent/10 border-accent/30'
                : 'bg-yellow-500/10 border-yellow-500/30'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  isWakeWordListening 
                    ? wakeWordDetected
                      ? 'bg-green-500 animate-pulse'
                      : 'bg-accent animate-pulse'
                    : 'bg-yellow-500'
                }`} />
                <span className={`text-sm ${
                  isWakeWordListening 
                    ? wakeWordDetected
                      ? 'text-green-400'
                      : 'text-accent'
                    : 'text-yellow-400'
                }`}>
                  {wakeWordDetected 
                    ? 'Wake word detected! Listening...'
                    : isWakeWordListening 
                      ? 'Listening for wake word...'
                      : 'Initializing microphone...'
                  }
                </span>
              </div>
              
              {isWakeWordListening && !wakeWordDetected && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex gap-1">
                    {[...Array(4)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-1 bg-accent/50 rounded-full"
                        animate={{ height: [4, 12, 4] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-[var(--text-tertiary)]">
                    Try saying "{wakeWord}"
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wake Word Configuration */}
      <div className="mb-6">
        <label className="block text-sm text-[var(--text-secondary)] mb-2">
          <Settings2 className="w-4 h-4 inline mr-2" />
          Wake Word
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={customWakeWord}
            onChange={(e) => setCustomWakeWord(e.target.value)}
            placeholder="Enter wake word..."
            className="input-field flex-1"
            data-testid="wake-word-input"
          />
          <button
            onClick={handleWakeWordChange}
            disabled={customWakeWord.trim().toLowerCase() === wakeWord}
            className="btn-secondary px-4 disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
        
        {/* Wake word suggestions */}
        <div className="flex flex-wrap gap-2 mt-3">
          {wakeWordSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => {
                setCustomWakeWord(suggestion);
                setWakeWord(suggestion);
                toast.success(`Wake word set to "${suggestion}"`);
              }}
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                wakeWord === suggestion
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-[var(--bg-card)] text-[var(--text-tertiary)] hover:bg-[var(--bg-card-hover)]'
              }`}
            >
              "{suggestion}"
            </button>
          ))}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-400 font-medium mb-1">Privacy Notice</p>
            <p className="text-xs text-[var(--text-tertiary)]">
              When enabled, your microphone will continuously listen for the wake word. 
              Audio is processed locally in your browser and is not recorded or sent to any server 
              until you speak to AIRA after the wake word is detected. 
              This feature resets each session for your privacy.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Warning Modal */}
      <AnimatePresence>
        {showPrivacyWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPrivacyWarning(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-[var(--text-primary)]">
                    Enable Always Listening?
                  </h4>
                  <p className="text-sm text-[var(--text-tertiary)]">
                    This requires microphone access
                  </p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <p className="text-sm text-[var(--text-secondary)]">
                  When enabled:
                </p>
                <ul className="text-sm text-[var(--text-tertiary)] space-y-2">
                  <li className="flex items-start gap-2">
                    <Mic className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                    <span>Your microphone will continuously listen for "{wakeWord}"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Audio is processed locally - nothing is recorded until you speak after the wake word</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Volume2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>You can disable this anytime from Settings</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPrivacyWarning(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEnable}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Mic className="w-4 h-4" />
                  Enable
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
}
